import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Radio } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { path } from 'ramda';
import { Profil, ServiceType, ServiceTypesGroupingModeValues, UserFilter } from 'types';

import Button from 'components/molecules/button';
import Input from 'components/organisms/input';
import { useIntl } from 'common/config/i18n';
import { StyledBlueLink } from 'common/styling/mixins';
import { ClearStateSliceActions } from 'common/config/store';
import FiltersCard from 'components/organisms/filters-card';
import InputSelect from 'components/organisms/select';

interface Props {
  title?: string;
  fetchListAction: (params: UserFilter) => void;
  exportListAction: (params: UserFilter) => void;
  canReadUsers?: boolean;
  canWriteUsers?: boolean;
  profils: Profil[];
  serviceTypes: ServiceType[];
  filterValues?: UserFilter;
}

type FormValues = Omit<UserFilter, 'pageNumber' | 'maxResultPage'>;

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const ServiceTypesWrapper = styled.div`
  padding: 5px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ServiceTypesRadio = styled.div`
  position: absolute;
  top: 55px;
  width: 350px;
`;

const Form = ({
  canReadUsers,
  canWriteUsers,
  profils,
  serviceTypes,
  fetchListAction,
  exportListAction,
  filterValues,
}: Props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const initialValues: FormValues = {
    firstName: filterValues?.firstName || undefined,
    lastName: filterValues?.lastName || undefined,
    profileID: filterValues?.profileID || undefined,
    groupName: filterValues?.groupName || undefined,
    serviceTypesGroupingMode: filterValues?.serviceTypesGroupingMode || ServiceTypesGroupingModeValues.OU,
    trainTypeIDs: filterValues?.trainTypeIDs || undefined,
  };

  useEffect(() => {
    dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User' }));
  }, [dispatch]);

  useEffect(() => {
    if (filterValues) {
      const { pageNumber: _, maxResultPage: __, ...formValues } = filterValues;
      formik.setValues(formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      fetchListAction({
        ...values,
        pageNumber: filterValues?.pageNumber,
        maxResultPage: filterValues?.maxResultPage,
      });
    },
  });

  const onReset = () => {
    formik.setValues(initialValues);
    fetchListAction({
      ...initialValues,
      pageNumber: 1,
      maxResultPage: 10,
    });
  };

  const selectProfilsData = [
    {
      id: 'profils',
      list:
        profils?.map((profil) => ({
          id: profil.id,
          name: profil.name,
        })) || [],
    },
  ];

  const inputSelectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        serviceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <FiltersCard
        title={t('UserPage', 'USER_RESEARCH_PAGE_H2')}
        firstPart={
          <>
            <Input
              type="text"
              name="lastName"
              upLabel={t('form', 'FORM_USER_NAME')}
              onChange={formik.handleChange}
              value={formik.values.lastName}
            />
            <Input
              type="text"
              name="firstName"
              upLabel={t('form', 'FORM_USER_SURNAME')}
              onChange={formik.handleChange}
              value={formik.values.firstName}
            />
            <Input
              type="text"
              name="groupName"
              upLabel={t('form', 'FORM_USER_GROUP')}
              onChange={formik.handleChange}
              value={formik.values.groupName}
            />
            <div>
              <UpLabel>{t('form', 'FORM_USER_PROFIL')}</UpLabel>
              <InputSelect
                onChange={(id) => formik.setFieldValue('profileID', id)}
                defaultValue={formik.values.profileID}
                data={selectProfilsData}
                style={{ width: '200px', borderRadius: '5px' }}
              />
            </div>
            {serviceTypes.length > 1 && (
              <ServiceTypesWrapper>
                <UpLabel>{t('filtre', 'TYPE_CIRCULATION')}</UpLabel>
                <InputSelect
                  mode="multiple"
                  onChange={(ids) => formik.setFieldValue('trainTypeIDs', ids)}
                  value={formik.values.trainTypeIDs}
                  data={inputSelectServiceTypesData}
                  style={{ minWidth: '250px', borderRadius: '5px', marginRight: 10 }}
                  dropdownStyle={{ minWidth: '20%' }}
                  popupMatchSelectWidth
                  showSearch
                />
                <ServiceTypesRadio>
                  <Radio.Group
                    onChange={(e) => formik.setFieldValue('serviceTypesGroupingMode', e.target.value)}
                    value={formik.values.serviceTypesGroupingMode}
                  >
                    <Radio value={ServiceTypesGroupingModeValues.ET}>{t('common', 'and')}</Radio>
                    <Radio value={ServiceTypesGroupingModeValues.OU}>{t('common', 'or')}</Radio>
                  </Radio.Group>
                </ServiceTypesRadio>
              </ServiceTypesWrapper>
            )}
          </>
        }
        secondPart={
          <>
            <Button htmlType="submit" borderRadius="20px" width="130px">
              {t('filtre', 'SEARCH_FILTER_FILTER_BUTTON')}
            </Button>
            <StyledBlueLink onClick={onReset}>{t('Buttons', 'RESET_BUTTON')}</StyledBlueLink>
          </>
        }
        topRightPart={
          canReadUsers &&
          canWriteUsers && (
            <FileExcelOutlined
              style={{ fontSize: 40, color: 'rgb(35,127,78)' }}
              onClick={() =>
                exportListAction({
                  ...formik.values,
                  pageNumber: filterValues?.pageNumber,
                  maxResultPage: filterValues?.maxResultPage,
                })
              }
            />
          )
        }
      />
    </form>
  );
};
export default Form;
